import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/SearchSelect";
import { MultiSelect } from "@/components/MultiSelect";
import { Save, Send, Loader2, Camera, X, Plus, Trash2, Fuel } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b border-slate-100 pb-1">
    <span className="shrink-0 text-slate-400">{label}</span>
    <span className="text-right font-medium text-slate-800">{value}</span>
  </div>
);

const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const num = (v) => parseFloat(v) || 0;
const API = process.env.REACT_APP_BACKEND_URL;

export default function NuovoRapportino() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isApprover = ["admin", "approvatore"].includes(user?.role);
  const [commesse, setCommesse] = useState([]);
  const [listini, setListini] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("bozza");
  const [savedId, setSavedId] = useState(id || null);
  const [step, setStep] = useState(id ? 6 : 1);
  const [dayDraft, setDayDraft] = useState(null);
  const [showReview, setShowReview] = useState(false);

  const [commessaId, setCommessaId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("8");
  const [team, setTeam] = useState([]);
  const [vehIds, setVehIds] = useState([]);
  const [activities, setActivities] = useState([{ voce_id: "", quantity: "", notes: "" }]);   // lavorazioni: {voce_id, quantity, notes}
  const [matItems, setMatItems] = useState([{ material_id: "", quantity: "" }]);   // materiali ripetibili: {material_id, quantity}
  const [noMat, setNoMat] = useState(false);   // «Nessun materiale utilizzato»
  const [hourTypeId, setHourTypeId] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [fuelLines, setFuelLines] = useState([]);
  const [costFuel, setCostFuel] = useState(0);
  const [fuelPersons, setFuelPersons] = useState([]);

  useEffect(() => {
    if (!date) return;
    api.get(`/fuel/day-persons?date=${date}`).then((r) => setFuelPersons(r.data)).catch(() => {});
  }, [date]);

  const handleTeamChange = (next) => {
    const added = next.filter((pid) => !team.includes(pid));
    added.forEach((pid) => {
      const fp = fuelPersons.find((x) => x.employee_id === pid);
      if (fp) toast.info(`Verrà incluso il carburante di ${fp.employee_name}`);
    });
    setTeam(next);
  };

  useEffect(() => {
    Promise.all([
      api.get("/commesse"), api.get("/listini"), api.get("/team-members"),
      api.get("/vehicles"), api.get("/hour-types"),
    ]).then(([c, l, p, v, h]) => {
      setCommesse(c.data); setListini(l.data); setTeamMembers(p.data); setVehicles(v.data); setHourTypes(h.data);
    });
    if (id) {
      api.get(`/rapportini/${id}`).then(({ data }) => {
        setCommessaId(data.commessa_id); setDate(data.date); setHours(String(data.hours));
        setTeam(data.team.map((t) => t.id)); setVehIds(data.vehicles.map((v) => v.id));
        setStatus(data.status); setSavedId(data.id); setStep(6);
        setHourTypeId(data.hour_type_id || ""); setNotes(data.notes || ""); setPhotos(data.photos || []);
        setFuelLines(data.fuel_lines || []); setCostFuel(data.cost_fuel || 0);
        const acts = (data.vdc || []).map((x) => ({ voce_id: x.voce_id, quantity: String(x.quantity), notes: x.notes || "" }));
        setActivities(acts.length ? acts : [{ voce_id: "", quantity: "", notes: "" }]);
        const mi = (data.materials || []).map((x) => ({ material_id: x.material_id, quantity: String(x.quantity) }));
        setMatItems(mi.length ? mi : [{ material_id: "", quantity: "" }]);
        setNoMat(false);
      }).catch(() => toast.error("Rapportino non trovato"));
    }
  }, [id]);

  useEffect(() => {
    if (!id && !hourTypeId && hourTypes.length) {
      const def = hourTypes.find((h) => h.is_default);
      if (def) setHourTypeId(def.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourTypes, id]);

  useEffect(() => {
    if (id) { setDayDraft(null); return; }
    api.get("/rapportini").then(({ data }) => {
      const d = (data || []).find((r) => r.date === date && r.status === "bozza");
      setDayDraft(d || null);
    }).catch(() => {});
  }, [id, date]);

  const commessa = commesse.find((c) => c.id === commessaId);
  const listino = useMemo(() => listini.find((l) => l.id === commessa?.listino_id), [listini, commessa]);
  const voci = listino?.voci || [];
  const passiveVoci = useMemo(
    () => listini.filter((l) => l.kind === "passivo").flatMap((l) => (l.voci || []).map((v) => ({ ...v, listino_name: l.name }))),
    [listini]
  );

  const totals = useMemo(() => {
    const revenue = activities.reduce((s, a) => { const v = voci.find((x) => x.id === a.voce_id); return s + (v ? num(a.quantity) * v.unit_price : 0); }, 0);
    const costP = teamMembers.filter((p) => team.includes(p.id)).reduce((s, p) => {
      const hc = p.hourly_costs || {};
      const rate = hc[hourTypeId] != null ? hc[hourTypeId] : p.cost_per_hour;
      return s + (parseFloat(rate) || 0);
    }, 0) * num(hours);
    const costV = vehicles.filter((v) => vehIds.includes(v.id)).reduce((s, v) => s + v.cost_per_hour, 0) * num(hours);
    const costM = matItems.reduce((s, it) => { const m = passiveVoci.find((v) => v.id === it.material_id); return s + (m ? num(it.quantity) * m.unit_price : 0); }, 0);
    const cost = costP + costV + costM + (costFuel || 0);
    return { revenue, cost, margin: revenue - cost };
  }, [voci, activities, teamMembers, team, hours, hourTypeId, vehicles, vehIds, passiveVoci, matItems, costFuel]);

  const addActivity = () => setActivities((p) => [...p, { voce_id: "", quantity: "", notes: "" }]);
  const removeActivity = (idx) => setActivities((p) => p.filter((_, i) => i !== idx));
  const updateActivity = (idx, patch) => setActivities((p) => p.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  const addMat = () => setMatItems((p) => [...p, { material_id: "", quantity: "" }]);
  const removeMat = (idx) => setMatItems((p) => p.filter((_, i) => i !== idx));
  const updateMat = (idx, patch) => setMatItems((p) => p.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const materialsChosen = passiveVoci.length === 0 || noMat || matItems.some((it) => it.material_id && num(it.quantity) > 0);

  const buildPayload = () => ({
    commessa_id: commessaId, hour_type_id: hourTypeId, date, hours: num(hours), team, vehicles: vehIds, notes, photos,
    vdc: activities.filter((a) => a.voce_id && num(a.quantity) > 0).map((a) => ({ voce_id: a.voce_id, quantity: num(a.quantity), notes: (a.notes || "").trim() })),
    materials: noMat ? [] : matItems.filter((it) => it.material_id && num(it.quantity) > 0).map((it) => ({ material_id: it.material_id, quantity: num(it.quantity) })),
  });

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await api.post("/uploads/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data.path]);
      } catch { toast.error("Errore caricamento foto"); }
    }
    e.target.value = "";
  };

  const save = async () => {
    if (!commessaId) { toast.error("Seleziona una commessa"); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = savedId ? await api.put(`/rapportini/${savedId}`, payload) : await api.post("/rapportini", payload);
      setSavedId(res.data.id); setStatus(res.data.status);
      setCostFuel(res.data.cost_fuel || 0); setFuelLines(res.data.fuel_lines || []);
      toast.success(res.data.status === "trasmesso" ? "Modifiche salvate" : "Bozza salvata");
      return res.data.id;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    const rid = await save();
    if (!rid) return;
    try {
      await api.post(`/rapportini/${rid}/submit`);
      toast.success("Rapportino trasmesso");
      navigate("/miei-rapportini");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const confirmSubmit = async () => { setShowReview(false); await submit(); };

  const locked = status === "trasmesso" && !isApprover;
  const adminEditing = status === "trasmesso" && isApprover;

  const saveEdit = async () => {
    const rid = await save();
    if (rid) navigate("/rapportini");
  };

  // Autosalvataggio bozza in compilazione (silenzioso, debounce 2s)
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const autoTimer = useRef(null);
  const autosaveNow = async () => {
    if (!commessaId) return;
    try {
      const payload = buildPayload();
      const res = savedId ? await api.put(`/rapportini/${savedId}`, payload) : await api.post("/rapportini", payload);
      setSavedId(res.data.id); setStatus(res.data.status);
      setCostFuel(res.data.cost_fuel || 0); setFuelLines(res.data.fuel_lines || []);
      setAutoSavedAt(new Date());
    } catch (e) { /* salvataggio silenzioso: nessun toast */ }
  };
  useEffect(() => {
    if (status !== "bozza" || locked || adminEditing || !commessaId || saving) return;
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => { autosaveNow(); }, 2000);
    return () => { if (autoTimer.current) clearTimeout(autoTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commessaId, date, hours, hourTypeId, team, vehIds, activities, matItems, noMat, notes, photos]);

  const advance = (s) => {
    if (s === 1) {
      if (!commessaId) { toast.error("Seleziona una commessa"); return; }
      if (num(hours) <= 0) { toast.error("Inserisci le ore lavorate"); return; }
      if (hourTypes.length > 0 && !hourTypeId) { toast.error("Seleziona il tipo di ore"); return; }
    }
    if (s === 2 && team.length === 0) { toast.error("Seleziona almeno una persona"); return; }
    if (s === 5 && !materialsChosen) { toast.error("Indica i materiali utilizzati o seleziona «Nessun materiale utilizzato»"); return; }
    setStep((cur) => Math.max(cur, s + 1));
  };

  const hourType = hourTypes.find((h) => h.id === hourTypeId);
  const teamNames = teamMembers.filter((p) => team.includes(p.id)).map((p) => p.name);
  const vehNames = vehicles.filter((v) => vehIds.includes(v.id)).map((v) => (v.plate ? `${v.name} (${v.plate})` : v.name));
  const actLines = activities.filter((a) => a.voce_id && num(a.quantity) > 0).map((a) => { const v = voci.find((x) => x.id === a.voce_id); return { desc: v?.description || "", qty: a.quantity, unit: v?.unit || "", notes: a.notes }; });
  const matLines = noMat ? [] : matItems.filter((it) => it.material_id && num(it.quantity) > 0).map((it) => { const m = passiveVoci.find((v) => v.id === it.material_id); return m ? { desc: m.description, qty: it.quantity, unit: m.unit } : null; }).filter(Boolean);

  if (!id && dayDraft) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card className="rounded-lg border-amber-200 bg-amber-50/40 p-6 shadow-sm" data-testid="day-draft-block">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Bozza già presente per oggi</h1>
          <p className="mt-2 text-sm text-slate-600">
            Per questa giornata esiste già una bozza di rapportino, alimentata automaticamente dai dati di <b>check-in</b> e delle <b>richieste carburante</b>. Riprendi quella bozza invece di crearne una nuova, così non perdi quei dati.
          </p>
          <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-sm">
            <p className="font-medium text-slate-900">{dayDraft.commessa_name || "Rapportino"}{dayDraft.commessa_code ? ` · ${dayDraft.commessa_code}` : ""}</p>
            <p className="text-xs text-slate-500">Data: {new Date(dayDraft.date + "T00:00:00").toLocaleDateString("it-IT")}</p>
          </div>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => navigate("/miei-rapportini")} data-testid="day-draft-goto-list" className="rounded-md">I miei rapportini</Button>
            <Button onClick={() => navigate(`/rapportino/${dayDraft.id}`)} data-testid="resume-day-draft-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Riprendi la bozza</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{savedId ? "Modifica rapportino" : "Nuovo rapportino"}</h1>
        <p className="mt-1 text-sm text-slate-500">Compila i dati dell'intervento. I ricavi e i costi vengono calcolati automaticamente.</p>
      </div>

      {locked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="locked-banner">
          Rapportino trasmesso e bloccato. Chiedi al responsabile di sbloccarlo per modificarlo.
        </div>
      )}

      <fieldset disabled={locked} className="min-w-0 space-y-6">
        <Card className="rounded-lg border-slate-200 p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <Label>Commessa</Label>
              <div className="mt-1.5">
                <SearchSelect
                  testid="rap-commessa-select"
                  options={commesse.map((c) => ({ value: c.id, label: `${c.name}${c.code ? ` · ${c.code}` : ""}`, keywords: c.client }))}
                  value={commessaId}
                  onChange={setCommessaId}
                  emptyLabel="Seleziona commessa"
                  placeholder="Cerca commessa..."
                />
              </div>
              {commessa && !listino && <p className="mt-1.5 text-xs text-rose-500">Questa commessa non ha un listino associato: nessun ricavo calcolabile.</p>}
            </div>
            <div>
              <Label htmlFor="rap-date">Data</Label>
              <Input id="rap-date" type="date" data-testid="rap-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="rap-hours">Ore lavorate</Label>
              <Input id="rap-hours" type="number" step="0.5" data-testid="rap-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1.5" />
            </div>
            <div className="sm:col-span-3">
              <Label>Tipo di ore</Label>
              <div className="mt-1.5">
                <SearchSelect
                  testid="rap-hourtype-select"
                  options={hourTypes.map((h) => ({ value: h.id, label: h.name }))}
                  value={hourTypeId}
                  onChange={setHourTypeId}
                  emptyLabel="Seleziona tipo di ore"
                  placeholder="Cerca tipo..."
                />
              </div>
              {hourTypes.length === 0 && <p className="mt-1.5 text-xs text-slate-400">Nessun tipo di ore configurato (Anagrafiche → Tipi di ore).</p>}
            </div>
          </div>
          {step === 1 && (<div className="mt-4 flex justify-end"><Button onClick={() => advance(1)} data-testid="rap-next-1" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div>)}
        </Card>

        <Card className={`rounded-lg border-slate-200 p-5 shadow-sm ${step < 2 ? "pointer-events-none select-none opacity-40" : ""}`}>
          <h2 className="mb-3 font-heading font-bold text-slate-900">Componenti Squadra {step < 2 && <span className="ml-1 text-xs font-normal text-slate-400">— completa la sezione precedente</span>}</h2>
          <MultiSelect
            testid="rap-team"
            options={teamMembers.map((p) => ({ value: p.id, label: `${p.name} — ${fmtEur(p.cost_per_hour)}/h` }))}
            value={team}
            onChange={handleTeamChange}
            allLabel="Nessuna persona selezionata"
            placeholder="Cerca persona..."
          />
          {team.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="rap-team-chips">
              {team.map((id) => { const p = teamMembers.find((x) => x.id === id); return p ? <span key={id} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs text-[#002FA7]">{p.name}</span> : null; })}
            </div>
          )}
          {step === 2 && (<div className="mt-4 flex justify-end"><Button onClick={() => advance(2)} data-testid="rap-next-2" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div>)}
        </Card>

        <Card className={`rounded-lg border-slate-200 p-5 shadow-sm ${step < 3 ? "pointer-events-none select-none opacity-40" : ""}`}>
          <h2 className="mb-3 font-heading font-bold text-slate-900">Mezzi in uso {step < 3 && <span className="ml-1 text-xs font-normal text-slate-400">— completa la sezione precedente</span>}</h2>
          <MultiSelect
            testid="rap-vehicles"
            options={vehicles.map((v) => ({ value: v.id, label: `${v.name}${v.plate ? ` (${v.plate})` : ""} — ${fmtEur(v.cost_per_hour)}/h`, keywords: v.plate }))}
            value={vehIds}
            onChange={setVehIds}
            allLabel="Nessun mezzo selezionato"
            placeholder="Cerca mezzo..."
          />
          {vehIds.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="rap-vehicles-chips">
              {vehIds.map((id) => { const v = vehicles.find((x) => x.id === id); return v ? <span key={id} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs text-[#002FA7]">{v.name}</span> : null; })}
            </div>
          )}
          {step === 3 && (<div className="mt-4 flex justify-end"><Button onClick={() => advance(3)} data-testid="rap-next-3" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div>)}
        </Card>

        <Card className={`rounded-lg border-slate-200 p-5 shadow-sm ${step < 4 ? "pointer-events-none select-none opacity-40" : ""}`}>
          <h2 className="mb-3 font-heading font-bold text-slate-900">Lavorazioni eseguite {step < 4 && <span className="ml-1 text-xs font-normal text-slate-400">— completa la sezione precedente</span>}</h2>
          {voci.length === 0 ? (
            <p className="text-sm text-slate-400">Seleziona una commessa con un listino per inserire le lavorazioni.</p>
          ) : (
            <div className="space-y-4" data-testid="rap-vdc">
              {activities.map((act, idx) => {
                const v = voci.find((x) => x.id === act.voce_id);
                const lineTotal = v ? num(act.quantity) * v.unit_price : 0;
                return (
                  <div key={idx} className="rounded-lg border border-slate-200 p-3" data-testid={`rap-activity-${idx}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Attività {idx + 1}</p>
                      {activities.length > 1 && <button type="button" onClick={() => removeActivity(idx)} data-testid={`rap-activity-remove-${idx}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                    <Label className="text-xs text-slate-500">VDC</Label>
                    <div className="mt-1.5">
                      <SearchSelect
                        testid={`rap-activity-vdc-${idx}`}
                        options={voci.map((x) => ({ value: x.id, label: `${x.description} (${fmtEur(x.unit_price)}/${x.unit})` }))}
                        value={act.voce_id}
                        onChange={(val) => updateActivity(idx, { voce_id: val })}
                        emptyLabel="Seleziona VDC"
                        placeholder="Cerca VDC..."
                      />
                    </div>
                    <div className="mt-3 flex items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Quantità{v ? ` (${v.unit})` : ""}</Label>
                        <Input type="number" step="0.01" placeholder="0" data-testid={`rap-activity-qty-${idx}`} value={act.quantity} onChange={(e) => updateActivity(idx, { quantity: e.target.value })} className="mt-1.5" />
                      </div>
                      <div className="pb-2 text-right text-sm whitespace-nowrap">
                        <span className="text-slate-400">Ricavo </span><span className="font-semibold text-emerald-600">{fmtEur(lineTotal)}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs text-slate-500">Note lavorazione</Label>
                      <textarea data-testid={`rap-activity-notes-${idx}`} value={act.notes} onChange={(e) => updateActivity(idx, { notes: e.target.value })} rows={2} placeholder="Note su questa lavorazione..." className="mt-1.5 w-full rounded-md border border-slate-200 p-2.5 text-sm focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
                    </div>
                  </div>
                );
              })}
              <Button type="button" variant="outline" onClick={addActivity} data-testid="rap-add-activity" className="w-full rounded-md border-dashed"><Plus className="mr-2 h-4 w-4" /> Aggiungi altra attività</Button>
            </div>
          )}
          {step === 4 && (<div className="mt-4 flex justify-end"><Button onClick={() => advance(4)} data-testid="rap-next-4" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div>)}
        </Card>

        <Card className={`rounded-lg border-slate-200 p-5 shadow-sm ${step < 5 ? "pointer-events-none select-none opacity-40" : ""}`}>
          <h2 className="mb-3 font-heading font-bold text-slate-900">Materiali utilizzati {step < 5 && <span className="ml-1 text-xs font-normal text-slate-400">— completa la sezione precedente</span>}</h2>
          {passiveVoci.length === 0 ? (
            <p className="text-sm text-slate-400">Nessun materiale disponibile. Crea un listino passivo in Anagrafiche → Listini.</p>
          ) : (
            <>
              <label className="mb-3 flex items-center gap-2 text-sm text-slate-700" data-testid="rap-materials-none-toggle">
                <input type="checkbox" checked={noMat} onChange={(e) => setNoMat(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]" />
                Nessun materiale utilizzato
              </label>
              {noMat ? (
                <p className="text-sm text-slate-500" data-testid="rap-materials-none">Nessun materiale utilizzato per questo intervento.</p>
              ) : (
                <div className="space-y-4" data-testid="rap-materials">
                  {matItems.map((it, idx) => {
                    const m = passiveVoci.find((v) => v.id === it.material_id);
                    const lineTotal = m ? num(it.quantity) * m.unit_price : 0;
                    return (
                      <div key={idx} className="rounded-lg border border-slate-200 p-3" data-testid={`rap-material-${idx}`}>
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Materiale {idx + 1}</p>
                          {matItems.length > 1 && <button type="button" onClick={() => removeMat(idx)} data-testid={`rap-material-remove-${idx}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>}
                        </div>
                        <Label className="text-xs text-slate-500">Materiale</Label>
                        <div className="mt-1.5">
                          <SearchSelect
                            testid={`rap-material-select-${idx}`}
                            options={passiveVoci.map((x) => ({ value: x.id, label: `${x.description} (${fmtEur(x.unit_price)}/${x.unit})`, keywords: x.listino_name }))}
                            value={it.material_id}
                            onChange={(val) => updateMat(idx, { material_id: val })}
                            emptyLabel="Seleziona materiale"
                            placeholder="Cerca materiale..."
                          />
                        </div>
                        <div className="mt-3 flex items-end gap-3">
                          <div className="flex-1">
                            <Label className="text-xs text-slate-500">Quantità{m ? ` (${m.unit})` : ""}</Label>
                            <Input type="number" step="0.01" placeholder="0" data-testid={`rap-material-qty-${idx}`} value={it.quantity} onChange={(e) => updateMat(idx, { quantity: e.target.value })} className="mt-1.5" />
                          </div>
                          <div className="pb-2 text-right text-sm whitespace-nowrap">
                            <span className="text-slate-400">Costo </span><span className="font-semibold text-rose-600">{fmtEur(lineTotal)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Button type="button" variant="outline" onClick={addMat} data-testid="rap-add-material" className="w-full rounded-md border-dashed"><Plus className="mr-2 h-4 w-4" /> Aggiungi altro materiale</Button>
                </div>
              )}
            </>
          )}
          {step === 5 && (<div className="mt-4 flex justify-end"><Button onClick={() => advance(5)} data-testid="rap-next-5" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">Avanti</Button></div>)}
        </Card>

        <Card className={`rounded-lg border-slate-200 p-5 shadow-sm ${step < 6 ? "pointer-events-none select-none opacity-40" : ""}`}>
          <h2 className="mb-3 font-heading font-bold text-slate-900">Note e foto <span className="ml-1 text-xs font-normal text-slate-400">(facoltativo)</span>{step < 6 && <span className="ml-1 text-xs font-normal text-slate-400">— completa la sezione precedente</span>}</h2>
          <textarea
            data-testid="rap-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Annotazioni sull'intervento..."
            className="w-full rounded-md border border-slate-200 p-3 text-sm focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
          />
          <div className="mt-4">
            <input id="rap-photo-input" type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" data-testid="rap-photo-input" />
            <Button type="button" variant="outline" onClick={() => document.getElementById("rap-photo-input").click()} data-testid="rap-photo-btn" className="rounded-md">
              <Camera className="mr-2 h-4 w-4" /> Aggiungi foto (camera o galleria)
            </Button>
            {photos.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="rap-photos">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={`${API}/api/files/${p}`} alt="foto" className="h-20 w-20 rounded-md object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute -right-1 -top-1 rounded-full bg-rose-600 p-0.5 text-white"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </fieldset>

      {fuelLines.length > 0 && (
        <Card className="mt-6 rounded-lg border-amber-200 bg-amber-50/40 p-5 shadow-sm" data-testid="rap-fuel-card">
          <div className="mb-2 flex items-center gap-2">
            <Fuel className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading font-bold text-slate-900">Carburante autorizzato</h2>
          </div>
          <p className="mb-3 text-xs text-slate-500">Aggiunto automaticamente dalle richieste carburante approvate per questa giornata. Si aggiorna con l'importo effettivo dopo il caricamento dello scontrino.</p>
          <div className="space-y-1.5">
            {fuelLines.map((f, i) => (
              <div key={i} className="flex items-center justify-between border-b border-amber-100 py-1.5 text-sm last:border-0">
                <span className="text-slate-700">{f.vehicle_name}{f.vehicle_plate ? ` (${f.vehicle_plate})` : ""}{f.actual == null && <span className="ml-2 text-xs italic text-amber-600">importo autorizzato · in attesa scontrino</span>}</span>
                <span className="font-medium text-rose-600">{fmtEur(f.cost)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between border-t border-amber-200 pt-2 text-sm font-semibold">
            <span className="text-slate-600">Totale carburante</span>
            <span className="text-rose-600" data-testid="rap-fuel-total">{fmtEur(costFuel)}</span>
          </div>
        </Card>
      )}

      <Card className="mt-6 rounded-lg border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-3 gap-2 text-center sm:gap-4">
          <div><p className="text-xs uppercase tracking-wide text-slate-400">Ricavi</p><p className="font-heading text-base font-extrabold whitespace-nowrap text-[#002FA7] sm:text-xl" data-testid="rap-revenue">{fmtEur(totals.revenue)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-slate-400">Costi</p><p className="font-heading text-base font-extrabold whitespace-nowrap text-[#002FA7] sm:text-xl" data-testid="rap-cost">{fmtEur(totals.cost)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-slate-400">Margine</p><p className={`font-heading text-base font-extrabold whitespace-nowrap sm:text-xl ${totals.margin < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="rap-margin">{fmtEur(totals.margin)}</p></div>
        </div>
      </Card>

      {adminEditing && step === 6 && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-amber-800">Stai modificando un rapportino <b>trasmesso</b> come responsabile. Le modifiche restano trasmesse.</p>
          <Button onClick={saveEdit} disabled={saving} data-testid="rap-admin-save-btn" className="shrink-0 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva modifiche
          </Button>
        </div>
      )}

      {!locked && !adminEditing && step === 6 && (
        <div className="mt-6 flex flex-col items-end gap-2">
          {status === "bozza" && (
            <p className="text-xs text-slate-400" data-testid="rap-autosave-indicator">
              {autoSavedAt ? `Bozza salvata automaticamente · ${autoSavedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "Salvataggio automatico attivo"}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={save} disabled={saving} data-testid="rap-save-btn" className="rounded-md">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva bozza
            </Button>
            <Button onClick={() => setShowReview(true)} disabled={saving} data-testid="rap-submit-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
              <Send className="mr-2 h-4 w-4" /> Rivedi e trasmetti
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="rap-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Riepilogo rapportino</DialogTitle>
            <DialogDescription>Controlla i dati prima di trasmettere. Una volta trasmesso il rapportino sarà bloccato.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3 text-center">
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Ricavi</p><p className="font-heading font-bold text-[#002FA7]">{fmtEur(totals.revenue)}</p></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Costi</p><p className="font-heading font-bold text-[#002FA7]">{fmtEur(totals.cost)}</p></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-400">Margine</p><p className={`font-heading font-bold ${totals.margin < 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtEur(totals.margin)}</p></div>
            </div>
            <ReviewRow label="Commessa" value={commessa ? `${commessa.name}${commessa.code ? ` · ${commessa.code}` : ""}` : "—"} />
            <ReviewRow label="Data" value={new Date(date + "T00:00:00").toLocaleDateString("it-IT")} />
            <ReviewRow label="Ore" value={`${hours} h${hourType ? ` · ${hourType.name}` : ""}`} />
            <ReviewRow label="Componenti squadra" value={teamNames.length ? teamNames.join(", ") : "—"} />
            <ReviewRow label="Mezzi" value={vehNames.length ? vehNames.join(", ") : "Nessuno"} />
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Lavorazioni</p>
              {actLines.length ? actLines.map((a, i) => (
                <div key={i} className="border-b border-slate-100 py-1">
                  <p className="text-slate-800">{a.desc} — {a.qty} {a.unit}</p>
                  {a.notes && <p className="text-xs italic text-slate-400">{a.notes}</p>}
                </div>
              )) : <p className="text-slate-400">—</p>}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Materiali</p>
              {noMat
                ? <p className="text-slate-600">Nessun materiale utilizzato</p>
                : matLines.length ? matLines.map((m, i) => <p key={i} className="border-b border-slate-100 py-1 text-slate-800">{m.desc} — {m.qty} {m.unit}</p>) : <p className="text-slate-400">—</p>}
            </div>
            {costFuel > 0 && <ReviewRow label="Carburante" value={fmtEur(costFuel)} />}
            <ReviewRow label="Note" value={notes || "—"} />
            <ReviewRow label="Foto" value={`${photos.length} allegate`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReview(false)} data-testid="rap-review-edit" className="rounded-md">Modifica</Button>
            <Button onClick={confirmSubmit} disabled={saving} data-testid="rap-confirm-submit" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Conferma e trasmetti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
